import React, { useEffect, useState } from 'react';
import { Col, Div, Icon, Input, Row, Text } from '@pinpoint/design-kit';
import isEmpty from 'lodash.isempty';

import CONSTANTS from 'src/store/constants';
import { Card, FileDrop, Button } from 'src/components';

import { cardProps as cardPropsActuarial, TopButtons as TopButtonsActuarial } from './actuarial_model';
import { cardProps as cardPropsClassifier, TopButtons as TopButtonsClassifier } from './classifier_model';
import { cardProps as cardPropsTarget, TopButtons as TopButtonsTarget } from './predict_forward';

/* eslint-disable react/prop-types */

function getCardProps(datasetTag) {
  switch (datasetTag) {
    case CONSTANTS.SEGMENT.TAG.ACTUARIAL:
      return cardPropsActuarial;
    case CONSTANTS.SEGMENT.TAG.HIT:
    case CONSTANTS.SEGMENT.TAG.MISS:
    case CONSTANTS.SEGMENT.TAG.VALIDATION:
      return cardPropsClassifier;
    case CONSTANTS.SEGMENT.TAG.TARGET:
      return cardPropsTarget;
    default:
      return cardPropsActuarial;
  }
}

function TopButtons({ datasetTag }) {
  switch (datasetTag) {
    case CONSTANTS.SEGMENT.TAG.ACTUARIAL:
      return <TopButtonsActuarial />;
    case CONSTANTS.SEGMENT.TAG.HIT:
    case CONSTANTS.SEGMENT.TAG.MISS:
    case CONSTANTS.SEGMENT.TAG.VALIDATION:
      return <TopButtonsClassifier />;
    case CONSTANTS.SEGMENT.TAG.TARGET:
      return <TopButtonsTarget />;
    default:
      return <TopButtonsActuarial />;
  }
}

function BottomButtons({ onBack }) {
  return (
    <Row>
      <Col size="12">
        <Div m={{ t: '1rem', x: '-0.5rem' }} d="flex" justify="space-between">
          <Button
            buttonStyle="primary"
            m={{ l: '0.5rem' }}
            onClick={() => {
              onBack();
            }}
            data-tip="go back"
            style={{
              flex: '0 0 auto',
            }}
          >
            <Icon color="white" name="LongLeft" size="1rem" m={{ r: '.5em' }} />
            BACK
          </Button>
        </Div>
      </Col>
    </Row>
  );
}

function Reonboarding({
  datasetTag,
  handleFileDrop,
  handleFileSelect,
  files,
  stageFiles,
  progress,
  uploadFileStatus,
  datasetName,
  isSuperAdmin,
  isAdmin,
  compState,
  triggerDetWorkflowStatus,
  // functions
  setDatasetName,
  getTagStyles,
  uploadFiles,
  setCompState,
  onDone,
  goBack,
  refreshes,
  defaultRefreshId,
  refreshId,
  setRefreshId,
}) {
  const [defaultRefresh, setDefaultRefresh] = useState({});
  const [refresh, setRefresh] = useState({});

  useEffect(() => {
    if (triggerDetWorkflowStatus === CONSTANTS.API.SUCCEEDED) onDone();
  }, [triggerDetWorkflowStatus, onDone]);

  useEffect(() => {
    if (isEmpty(defaultRefresh)) setDefaultRefresh(refreshes?.find((r) => r.name.includes(defaultRefreshId)));
    if (!refreshId) setRefreshId(defaultRefreshId);
    if (refreshId && !refresh?.name?.includes(refreshId))
      setRefresh(refreshes?.find((r) => r.name.includes(refreshId)));
  }, [defaultRefresh, defaultRefreshId, refresh?.name, refreshId, refreshes, setRefreshId]);

  return (
    <>
      <TopButtons datasetTag={datasetTag} />
      <Row>
        <Col size="12">
          <Card
            title="Select file to upload"
            iconProps={getCardProps(datasetTag).iconProps}
            titleProps={getCardProps(datasetTag).titleProps}
            titleTextProps={getCardProps(datasetTag).titleTextProps}
            cardProps={getCardProps(datasetTag).cardProps}
          >
            <Row p={{ l: '37px', r: '39px', t: '27px', b: '29px' }}>
              <Col size="5">
                <FileDrop
                  onDrop={handleFileDrop}
                  onSelect={handleFileSelect}
                  droppedFiles={files}
                  setDroppedFiles={stageFiles}
                  progress={progress}
                  uploadFileStatus={uploadFileStatus}
                />
              </Col>
              <Col size="7">
                <Text textSize="subtitle" m={{ b: '4px' }}>
                  Dataset Name
                </Text>
                <Input value={datasetName} disabled h="2.4rem" m={{ b: '14px' }} />
                <Text textSize="subtitle" m={{ b: '4px' }}>
                  Type
                </Text>
                <Text textSize="subtitle" m={{ b: '14px' }}>
                  <Div
                    d="flex"
                    align="center"
                    border="1px solid"
                    borderColor="gray500"
                    rounded="md"
                    p={{ x: '12px' }}
                    h="35.2px"
                  >
                    <Icon
                      name="Positive"
                      color="tagHit"
                      viewBox="0 0 50 50"
                      size=".875rem"
                      m={{ r: '.5em' }}
                      alignSelf="center"
                      {...getTagStyles(datasetTag)}
                    />
                    <Text textSize="body" textColor={getTagStyles(datasetTag).color}>
                      {CONSTANTS.SEGMENT.TAG_NAME[datasetTag]}
                    </Text>
                  </Div>
                </Text>
                {isSuperAdmin ? (
                  <Div>
                    Refresh
                    <Input isLoading={!refreshId} value={refresh?.name} disabled h="2.4rem" m={{ b: '14px' }} />
                  </Div>
                ) : null}
                <Div m={{ t: '40px' }} d="flex" justify="flex-end">
                  <Button
                    buttonStyle="primary"
                    m={{ r: '0rem' }}
                    isLoading={compState !== CONSTANTS.COMPONENT.VALIDATION.VALIDATED}
                    disabled={
                      !isAdmin ||
                      uploadFileStatus === CONSTANTS.API.STARTED ||
                      triggerDetWorkflowStatus === CONSTANTS.API.STARTED ||
                      files.length === 0 ||
                      !datasetName.trim() ||
                      compState !== CONSTANTS.COMPONENT.VALIDATION.VALIDATED ||
                      datasetName.trim().length > 120
                    }
                    loading={uploadFileStatus === CONSTANTS.API.STARTED}
                    onClick={() => {
                      setCompState(CONSTANTS.COMPONENT.OPERATION.STARTED);
                      uploadFiles(files, CONSTANTS.S3_PREFIX.ONPOINT_WORKFLOW);
                      setDatasetName(datasetName.trim());
                    }}
                  >
                    UPLOAD
                    <Icon name="UploadForward" viewBox="0 0 14 16" color="white" m={{ l: '0.5rem' }} size="0.875rem" />
                  </Button>
                </Div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
      <BottomButtons
        onBack={() => {
          goBack();
        }}
      />
    </>
  );
}

export default Reonboarding;
